/* @flow */

import type { $GeolocationPosition } from '../types/GeolocationPosition';
import type { $RequestStatus } from '../types/RequestStatus';
import { RequestStatus } from '../constants/RequestStatus';

export const ActionTypes = {
  DL_GET_CURRENT_POSITION_REQUEST: 'dl/geolocation/DL_GET_CURRENT_POSITION_REQUEST',
  DL_GET_CURRENT_POSITION_SUCCESS: 'dl/geolocation/DL_GET_CURRENT_POSITION_SUCCESS',
  DL_GET_CURRENT_POSITION_FAILURE: 'dl/geolocation/DL_GET_CURRENT_POSITION_FAILURE'
};

type $Action =
  | { type: typeof ActionTypes.DL_GET_CURRENT_POSITION_REQUEST, context: $GeolocationContext }
  | { type: typeof ActionTypes.DL_GET_CURRENT_POSITION_FAILURE, error: Error | PositionError }
  | { type: typeof ActionTypes.DL_GET_CURRENT_POSITION_SUCCESS, position: $GeolocationPosition };

export const getCurrentPositionRequest = (context: $GeolocationContext) => ({
  type: ActionTypes.DL_GET_CURRENT_POSITION_REQUEST,
  context
});

export const getCurrentPositionSuccess = (position: $GeolocationPosition) => ({
  type: ActionTypes.DL_GET_CURRENT_POSITION_SUCCESS,
  position
});

export const getCurrentPositionFailure = (error: string) => ({
  type: ActionTypes.DL_GET_CURRENT_POSITION_FAILURE,
  error
});

export const GeolocationContext = {
  SEARCH: 'search',
  ROUTING: 'routing'
};

export type $GeolocationContext = $Values<typeof GeolocationContext>;

export const getCurrentPosition = (
  context: $GeolocationContext = GeolocationContext.SEARCH
) => async (dispatch: Function) => {
  dispatch(getCurrentPositionRequest(context));

  if (!('geolocation' in navigator)) {
    return dispatch(getCurrentPositionFailure('Geolocation API not supported'));
  }

  const getCurrentPosition = () =>
    new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject));

  try {
    const { coords: { latitude, longitude } } = await getCurrentPosition();
    return dispatch(getCurrentPositionSuccess({ latitude, longitude }));
  }
  catch (error) {
    return dispatch(getCurrentPositionFailure(error.message));
  }
};

export type $GeolocationState = {
  status: $RequestStatus,
  context?: $GeolocationContext,
  position?: $GeolocationPosition,
  error?: string
};

const initialState: $GeolocationState = {
  status: RequestStatus.INIT
};

export default function reducer(state: $GeolocationState, action: $Action) {
  state = state || initialState;
  switch (action.type) {

    case ActionTypes.DL_GET_CURRENT_POSITION_REQUEST:
      return {
        ...state,
        status: RequestStatus.PENDING,
        context: action.context
      };

    case ActionTypes.DL_GET_CURRENT_POSITION_SUCCESS:
      return {
        ...state,
        position: action.position,
        status: RequestStatus.SUCCESS
      };

    case ActionTypes.DL_GET_CURRENT_POSITION_FAILURE:
      return {
        ...state,
        error: action.error,
        status: RequestStatus.FAILURE
     };

    default:
      return state;
  }
}
